import { useCallback, useRef, useState } from 'react';
import { isNil } from 'es-toolkit';

export type PersistedPreferenceCodec<T> = {
  readonly parse: (raw: string) => T;
  readonly serialize: (value: T) => string;
};

export function readPersistedPreference<T>(key: string | null, codec: PersistedPreferenceCodec<T>, fallback: T): T {
  if (typeof window === 'undefined' || isNil(key)) {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return isNil(raw) ? fallback : codec.parse(raw);
  } catch {
    return fallback;
  }
}

export function writePersistedPreference<T>(key: string | null, codec: PersistedPreferenceCodec<T>, value: T): void {
  if (typeof window === 'undefined' || isNil(key)) {
    return;
  }
  try {
    window.localStorage.setItem(key, codec.serialize(value));
  } catch {
    return;
  }
}

export type PersistedPreferenceUpdater<T> = (change: (previous: T) => T) => void;

export function usePersistedPreference<T>(
  key: string | null,
  codec: PersistedPreferenceCodec<T>,
  fallback: T,
): readonly [T, PersistedPreferenceUpdater<T>] {
  const [value, setValue] = useState(() => readPersistedPreference(key, codec, fallback));
  const [loadedKey, setLoadedKey] = useState(key);

  const latest = useRef(value);
  latest.current = value;
  const target = useRef({ key, codec });
  target.current = { key, codec };

  if (loadedKey !== key) {
    setLoadedKey(key);
    const restored = readPersistedPreference(key, codec, fallback);
    latest.current = restored;
    setValue(restored);
  }

  const update = useCallback<PersistedPreferenceUpdater<T>>((change) => {
    const next = change(latest.current);
    latest.current = next;
    setValue(next);
    writePersistedPreference(target.current.key, target.current.codec, next);
  }, []);

  return [value, update];
}
